import React from 'react';
import styled from 'styled-components';

// Define the styles using styled-components
const ContactContainer = styled.div`
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  color: #333;
  padding: 2.5rem;
  max-width: 900px;
  margin: 2rem auto;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  line-height: 1.6;

  @media (max-width: 768px) {
    padding: 1.5rem;
    margin: 1rem auto;
  }
`;

const ContactHeader = styled.header`
  text-align: center;
  margin-bottom: 2.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 2px solid #0056b3;
`;

const ContactH1 = styled.h1`
  color: #0056b3;
  font-size: 2.5rem;
  margin-bottom: 0.75rem;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ContactP = styled.p`
  line-height: 1.7;
  color: #555;
  font-size: 1.1rem;
  max-width: 80%;
  margin: 0 auto;

  @media (max-width: 768px) {
    max-width: 100%;
    font-size: 1rem;
  }
`;

const ContactSection = styled.section`
  margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
  color: #2e7d32; /* Darker green for better contrast */
  font-size: 1.6rem;
  margin-bottom: 1.2rem;
  border-bottom: 1px solid #eaeaea;
  padding-bottom: 0.6rem;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const ContactList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ContactItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 1.2rem;
  font-size: 1.1rem;
`;

const ContactIcon = styled.span`
  margin-right: 1.2rem;
  font-size: 1.5rem;
  color: #0056b3;
  min-width: 30px;
  text-align: center;
`;

const ContactLink = styled.a`
  text-decoration: none;
  color: #0056b3;
  transition: color 0.2s ease-in-out;
  font-weight: 500;

  &:hover {
    color: #2e7d32;
    text-decoration: underline;
  }
`;

const SocialList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  list-style: none;
  padding: 0;
  margin: 0;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const SocialItem = styled.li`
  display: flex;
  align-items: center;
`;

const SocialLink = styled(ContactLink)`
  font-size: 1.1rem;
`;

const AddressText = styled.p`
  margin: 0;
  line-height: 1.6;
`;

const MapLink = styled(ContactLink)`
  margin-top: 0.5rem;
  display: inline-block;
  font-size: 0.95rem;
`;

const ContactUs: React.FC = () => {
  return (
    <ContactContainer>
      <ContactHeader>
        <ContactH1>Contact Us</ContactH1>
        <ContactP>
          We're committed to providing exceptional service. Reach out to our dedicated Customer Support Team through any of the channels below.
        </ContactP>
      </ContactHeader>

      <ContactSection>
        <SectionTitle>Location</SectionTitle>
        <ContactList>
          <ContactItem>
            <ContactIcon>📍</ContactIcon>
            <div>
              <AddressText><strong>Ntinda Shopping Centre (Office: C30) Ntinda-Kampala </strong></AddressText>              <MapLink href="https://maps.google.com/?q=Three+Sisters+Complex+B27" target="_blank" rel="noopener noreferrer">
                View on Google Maps
              </MapLink>
            </div>
          </ContactItem>
        </ContactList>
      </ContactSection>

      <ContactSection>
        <SectionTitle>Working Hours</SectionTitle>
        <ContactList>
          <ContactItem>
            <ContactIcon>⏰</ContactIcon>
            <p>Monday to Friday: <strong>8:00 AM - 5:00 PM</strong></p>
          </ContactItem>
          <ContactItem>
            <ContactIcon>⏰</ContactIcon>
            <p>Saturday: <strong>9:00 AM - 1:00 PM</strong></p>
          </ContactItem>
          <ContactItem>
            <ContactIcon>⏰</ContactIcon>
            <p>Sunday and Public Holidays: <strong>Closed</strong></p>
          </ContactItem>
        </ContactList>
      </ContactSection>

      <ContactSection>
        <SectionTitle>Phone and WhatsApp</SectionTitle>
        <ContactList>
          <ContactItem>
            <ContactIcon>📞</ContactIcon> 
            <div>
              Office Line: <ContactLink href="tel:+256393002383">+256 393 002 383</ContactLink>
            </div>
          </ContactItem>
          <ContactItem>
            <ContactIcon>📱</ContactIcon>
            <div>
              Mobile Phone: <ContactLink href="tel:+256790807560">+256 790 807 560</ContactLink>
            </div>
          </ContactItem>
          <ContactItem>
            <ContactIcon>💬</ContactIcon>
            <div>
              WhatsApp: <ContactLink href="https://wa.me/+256774062514" target="_blank" rel="noopener noreferrer">256 774 062 514</ContactLink>
            </div>
          </ContactItem>
        </ContactList>
      </ContactSection>

      <ContactSection>
        <SectionTitle>Email</SectionTitle>
        <ContactItem>
          <ContactIcon>📧</ContactIcon>
          <ContactLink href="mailto:gremaglobal1@gmail.com">gremaglobal1@gmail.com</ContactLink>
        </ContactItem>
      </ContactSection>

      <ContactSection>
        <SectionTitle>Connect With Us</SectionTitle>
        <SocialList>
          <SocialItem>
            <ContactIcon>📘</ContactIcon>
            <SocialLink href="https://facebook.com/gremaglobal1" target="_blank" rel="noopener noreferrer">Facebook</SocialLink>
          </SocialItem>
          <SocialItem>
            <ContactIcon>🐦</ContactIcon>
            <SocialLink href="https://twitter.com/gremaglobal" target="_blank" rel="noopener noreferrer">Twitter (X)</SocialLink>
          </SocialItem>
          <SocialItem>
            <ContactIcon>💼</ContactIcon>
            <SocialLink href="https://www.linkedin.com/in/gremaglobal" target="_blank" rel="noopener noreferrer">LinkedIn</SocialLink>
          </SocialItem>
          <SocialItem>
            <ContactIcon>📸</ContactIcon>
            <SocialLink href="https://www.instagram.com/gremaglobal" target="_blank" rel="noopener noreferrer">Instagram</SocialLink>
          </SocialItem>
          <SocialItem>
            <ContactIcon>💃</ContactIcon>
            <SocialLink href="https://www.tiktok.com/@gremaglobal" target="_blank" rel="noopener noreferrer">TikTok</SocialLink>
          </SocialItem>
          <SocialItem>
            <ContactIcon>▶️</ContactIcon>
            <SocialLink href="https://www.youtube.com/@gremaglobal" target="_blank" rel="noopener noreferrer">YouTube</SocialLink>
          </SocialItem>
        </SocialList>
      </ContactSection>
    </ContactContainer>
  );
};

export default ContactUs;